import { Box, Collapse, Grid, Theme } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getSortedData, getUnique } from 'app/helpers/arrayHelpers';
import {
  selectFilters,
  selectGridFiltersExpanded,
  selectGridState,
} from 'app/components/Common/Grid/GridStateManagement/GridState.selectors';
import { useDispatch, useSelector } from 'react-redux';

import { CustomFilter } from 'app/ComponentLibrary/Filter/CustomFilter';
import { ILookupOptions } from 'store/redux-store/lookup/initialState';
import { MarginFilterSpecs, MarginFilterNames } from './MarginFilterUtils';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { SearchType } from 'app/models/GlobalSearch/GlobalSearch';

import { gridStateActions } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.redux';
import { lookupOptionSelector } from 'store/redux-store/lookup/lookup.selectors';
import { makeStyles } from 'tss-react/mui';
import { trackPromise } from 'react-promise-tracker';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SearchResultsChips } from '../SearchResultsChips';
import { getSearchFilterOptions, fetchDisciplineAndSpecialty } from 'app/services/SharedServices/SharedServices';
import { PositionOpts } from '../Order/FilterUtils';

const useStyles = makeStyles()((theme: Theme) => ({
  filterCollapse: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: '3px',
  },
  collapse: {
    paddingTop: '4px',
  },
}));

export const MarginOrdersFilter = (props: { onApplyFilter: (values: any, newSelectedChipFilters?: any) => void; suppressAutoReset?: boolean }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reset, getValues, setValue } = useFormContext();
  const gridStateFilters = useSelector(selectFilters);
  const gridState = useSelector(selectGridState);
  const loadedData = useSelector(lookupOptionSelector) as ILookupOptions;
  const filtersExpanded = useSelector(selectGridFiltersExpanded) ?? true;

  const handleChange = () => {
    dispatch(gridStateActions.setFiltersExpanded(!filtersExpanded));
  };

  const gridSpecs = useRef(
    new MarginFilterSpecs(
      {
        orderIdOpts: [],
        payPackageStatusOpts: [],
        jdhPostingStatusOpts: [],
        divisionOpts: [],
        facilityOpts: [],
        skillSetOpts: [],
        shiftOpts: [],
        positionOpts: [...PositionOpts],
      },
      gridStateFilters,
    ),
  );
  const [fields, setFields] = React.useState(() => {
    return gridSpecs.current.getMarginFilterSpecs();
  });

  const getMultiSelectOptions = (options, labelPrefix?, labelPostfix?, withoutAllOption?: boolean) => {
    const opts = (options || []).map((opt, index) => ({
      id: index + 1,
      ...opt,
    }));
    if (!withoutAllOption) opts.unshift({ id: 0, name: 'All', value: 'All' });
    return opts;
  };

  const fetchMarginFilterSpecs = async () => {
    // Load division options
    getSearchFilterOptions('divisions', true).then(res => {
      gridSpecs.current.filterSpecs.divisionOpts = getMultiSelectOptions(res || [], undefined, undefined, true);
      const fieldsSchema = gridSpecs.current.getMarginFilterSpecs();
      setFields(fieldsSchema);
    });

    // Load facility options
    getSearchFilterOptions('facility', true).then(res => {
      gridSpecs.current.filterSpecs.facilityOpts = getMultiSelectOptions(res || []);
      const fieldsSchema = gridSpecs.current.getMarginFilterSpecs();
      setFields(fieldsSchema);
    });

    // Load hierarchical skillset data
    const abortController = new AbortController();
    fetchDisciplineAndSpecialty('', abortController.signal, true).then(res => {
      if (res?.treeStructure?.length > 0) {
        gridSpecs.current.filterSpecs.skillSetOpts = res.treeStructure;
        const fieldsSchema = gridSpecs.current.getMarginFilterSpecs();
        setFields(fieldsSchema);
      }
    }).catch(error => {
      console.error('Error loading skillset data:', error);
    });

    // Load shift options
    getSearchFilterOptions('shiftlist', true).then(res => {
      gridSpecs.current.filterSpecs.shiftOpts = getMultiSelectOptions(res || [], undefined, undefined, true);
      const fieldsSchema = gridSpecs.current.getMarginFilterSpecs();
      setFields(fieldsSchema);
    });

    // Set static options
    const payPackageStatusOpts = getMultiSelectOptions([
      { name: 'Active', value: 1 },
      { name: 'Inactive', value: 2 }
    ], undefined, undefined, true);
    const jdhPostingStatusOpts = getMultiSelectOptions([
      { name: 'Pending', value: '1' },
      { name: 'Success', value: '2' },
      { name: 'Error', value: '3' },
    ], undefined, undefined, true);

    gridSpecs.current.filterSpecs = {
      ...gridSpecs.current.filterSpecs,
      payPackageStatusOpts,
      jdhPostingStatusOpts,
    };
    
    const fieldsSchema = gridSpecs.current.getMarginFilterSpecs();
    setFields(fieldsSchema);
  };

  const resetForm = () => {
    const defaultAppliedFilters = {
      payPackageStatus: [
        {
          id: 0,
          name: 'Active',
          value: 1,
        },
      ],
      positions: PositionOpts[1],
    };
    reset({ ...defaultAppliedFilters });
    // Reset pagination to page 1 when filters are reset/refreshed
    dispatch(gridStateActions.setPageNumber(1));
    dispatch(
      gridStateActions.setFilterAttributes({
        ...gridState.filterAttributes,
        filterObject: Object.entries(defaultAppliedFilters),
        selectedChipFilters: {},
        associatedGridName: gridState.gridTag,
      }),
    );
    props.onApplyFilter(defaultAppliedFilters);
  };

  const handleApplyFilter = useCallback((values, newSelectedChipFilters?) => {
    // For margin search, we don't want to mark chips as selected by default
    // Only pass selectedChipFilters if they are explicitly provided
    const selectedChipFilters = newSelectedChipFilters || {};
    props.onApplyFilter(values, selectedChipFilters);
  }, [dispatch, props]);

  useEffect(() => {
    if (loadedData) {
      trackPromise(fetchMarginFilterSpecs(), 'margin-filter-call');
    }
  }, [loadedData]);

  // Expand filters and hydrate the form whenever grid filters are (re)established
  useEffect(() => {
    dispatch(gridStateActions.setFiltersExpanded(true));
    try {
      const entries: any = (gridState as any)?.filterAttributes?.filterObject;
      if (entries && Array.isArray(entries) && entries.length > 0) {
        const restored = Object.fromEntries(entries);
        reset(restored);
        return;
      }
      // No restored filters present; ensure defaults once
      if (!props.suppressAutoReset && (!gridStateFilters?.filters || (Array.isArray(gridStateFilters?.filters) && gridStateFilters?.filters.length === 0))) {
        resetForm();
      }
    } catch {
      if (!props.suppressAutoReset) resetForm();
    }
  }, [dispatch, (gridState as any)?.filterAttributes?.filterObject, props.suppressAutoReset]);


  return (
    <Box sx={{ paddingLeft: '12px', paddingTop: '12px', marginTop: '12px' }}> 
      <Grid container spacing={2}>
      <Grid container wrap="nowrap" display="flex">
        <Grid item flex="1 1 auto">
            <Collapse timeout={200} in={filtersExpanded} collapsedSize={45} className={classes.collapse}>
              <CustomFilter
                fields={((): any[] => {
                  const allowedOrder: string[] = [
                    MarginFilterNames.orderId,
                    MarginFilterNames.payPackageStatus,
                    MarginFilterNames.jdhPostingStatus,
                    MarginFilterNames.division,
                    MarginFilterNames.facility,
                    MarginFilterNames.skillSet,
                    MarginFilterNames.shift,
                    MarginFilterNames.defaultTargetMargin,
                    MarginFilterNames.defaultWeeklyPay,
                    MarginFilterNames.actualTargetMargin,
                    MarginFilterNames.weeklyGrossPay,
                    MarginFilterNames.positions,
                  ] as unknown as string[];
                  const allowedSet = new Set(allowedOrder);
                  return [...fields]
                    .filter(spec => allowedSet.has(String(spec.name)))
                    .map(spec => {
                      if (spec.name === MarginFilterNames.actualTargetMargin) {
                        return { ...spec, placeholder: 'Margin', numberRangeType: 'percentage', type: spec.type };
                      }
                      if (spec.name === MarginFilterNames.weeklyGrossPay) {
                        return { ...spec, placeholder: 'Weekly Gross Pay', numberRangeType: 'currency', type: spec.type };
                      }
                      return spec;
                    })
                    .sort((a, b) => {
                      const ai = allowedOrder.indexOf(String(a.name));
                      const bi = allowedOrder.indexOf(String(b.name));
                      return (ai === -1 ? Number.MAX_SAFE_INTEGER : ai) - (bi === -1 ? Number.MAX_SAFE_INTEGER : bi);
                    });
                })()}
                justifyContent={'flex-start'}
                version2
                resetBtn
                disableFormSubmit
                analytics={{
                  caller: t('Margin Orders Filter'),
                  onApply: true,
                  properties: {
                    category: SearchType.orderpaypackage,
                  },
                }}
                onChangeValues={(name, value) => {
                  setValue(name, value);
                }}
                onApply={() => {
                  handleApplyFilter(getValues());
                }}
              />
            </Collapse>
        </Grid>
        <Grid item flex="0 0 auto" className={classes.filterCollapse}>
          <SearchPageIcons
            resetlbutton
            funnelButton
            checked={filtersExpanded}
            onResetForm={() => resetForm()}
            onAdvancedFilterClick={() => handleChange()}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ marginLeft: '-12px' }}>
        <SearchResultsChips 
          onApplyFilter={handleApplyFilter} 
          category="margin" 
          hideDelete={false}
          disableFormSubmit
        />
      </Grid>
      </Grid>
    </Box>
  );
};